import { FC, MouseEventHandler, ReactNode, useCallback, useState } from 'react';
import { DialogProps } from '@mui/material';
import { ButtonsSettings } from 'components/dialog/Dialog.styles';

type EventCallback = (event?: MouseEventHandler<HTMLButtonElement>) => void;

export type WrapperProps = {
  onConfirm?: (event?: MouseEventHandler<HTMLButtonElement>) => void;
  onCancel?: (event?: MouseEventHandler<HTMLButtonElement>) => void;
  onDiscard?: (event?: MouseEventHandler<HTMLButtonElement>) => void;
  open: boolean;
  children?: ReactNode;
  buttonsSettings?: ButtonsSettings;
  logsUrl?: string;
  filename?: string;
};

type DialogComponentProps = Omit<DialogProps & WrapperProps, 'open'>;

export default function useDialog<T extends DialogProps>(
  DialogComponent: FC<T>,
): [(show?: boolean) => void, (props: DialogComponentProps & Omit<T, 'open'>) => ReactNode, boolean] {
  const [open, setOpen] = useState(false);

  const onConfirmWrapper = useCallback((confirmCallback?: EventCallback) => {
    setOpen(false);
    if (confirmCallback) confirmCallback();
  }, []);

  const onCancelWrapper = useCallback((cancelCallback?: EventCallback) => {
    setOpen(false);
    if (cancelCallback) cancelCallback();
  }, []);

  const showModal = useCallback((show = true) => {
    setOpen(!!show);
  }, []);

  const WrapperComponent = useCallback(
    ({ onConfirm, onCancel, ...rest }: DialogComponentProps): ReactNode => (
      <DialogComponent
        {...(rest as T)}
        onConfirm={onConfirmWrapper.bind(null, onConfirm)}
        onCancel={onCancelWrapper.bind(null, onCancel)}
        open={open}
      />
    ),
    [DialogComponent, onCancelWrapper, onConfirmWrapper, open],
  );

  return [showModal, WrapperComponent, open];
}
